import { isAppError } from '@/lib/api/errors';

/** The two screens of sign-in: email + password, then the authenticator code. */
export type LoginStep = 'credentials' | 'code';

/**
 * Where the sign-in flow goes after a failed attempt, and what it says there.
 *
 * `message` is an i18n KEY, not prose — the caller runs it through `t()`. It is `null` when
 * moving to the code step says everything: a correct password on an MFA account is not an
 * error, so the verification screen opens without a red line on it.
 */
export type LoginErrorTreatment = {
  step: LoginStep;
  message:
    | 'auth.rateLimited'
    | 'auth.locked'
    | 'auth.disabled'
    | 'auth.totpRequired'
    | 'auth.invalidTotp'
    | 'auth.unavailable'
    | 'auth.invalid'
    | null;
};

/**
 * Classify a failed `POST /api/v1/admin/auth/login`, sent from `step`.
 *
 * be-tepco answers (internal/domain/users/service.go `Login`):
 *
 *   - password correct, no code sent → **422** `validation_error` with a `totp_code` detail
 *   - password correct, code wrong   → **401** `"Invalid MFA code."`
 *   - password wrong                 → **401**, no MFA wording, no `totp_code` detail
 *   - account locked                 → **403** `"Account temporarily locked due to failed attempts."`
 *   - account disabled               → **403** `"This account is disabled."`
 *
 * Keying on the structured `totp_code` detail is the durable channel: it survives any
 * rewording of the message. The 401 regex is kept for the wrong-code case, which carries no
 * field detail. A plain wrong password must surface "email or password is incorrect", never
 * a spurious authenticator prompt (FE-001) — which is why neither branch is a bare status
 * check. A wrong password answered from the code step (the password changed between the two
 * steps) goes back to the credentials step, because that is where it can be corrected.
 * A failure that never reached the server, or a 5xx, is not "incorrect credentials" and must
 * not say so; it leaves the user where they are, so nothing they typed is lost.
 */
export function classifyLoginError(err: unknown, step: LoginStep): LoginErrorTreatment {
  if (!isAppError(err)) return { step, message: 'auth.invalid' };

  if (err.is('rate_limited')) return { step, message: 'auth.rateLimited' };
  if (err.status === 0 || err.status >= 500) return { step, message: 'auth.unavailable' };
  if ((err.status === 401 || err.status === 403) && /lock/i.test(err.message)) {
    return { step: 'credentials', message: 'auth.locked' };
  }
  if (err.status === 403) return { step: 'credentials', message: 'auth.disabled' };

  // "We still need a code" — the first time it is the hand-off to the code step, not an error.
  const totpDetail = (err.details ?? []).some((d) => d.field === 'totp_code');
  if (totpDetail) return { step: 'code', message: step === 'code' ? 'auth.totpRequired' : null };

  // "The code you sent did not verify."
  if (err.status === 401 && /totp|mfa|2fa|authenticat|verification code/i.test(err.message)) {
    return { step: 'code', message: 'auth.invalidTotp' };
  }

  return { step: 'credentials', message: 'auth.invalid' };
}
